import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import { getModuleLabels } from "../../data/modules";
import { DeleteItem, InviteModal, SuccessModal } from "../../components/modal";
import InfoCard from "../../components/card/infocard";
import StaffListModal from "../../components/company-settings/StaffListModal";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";

const DEFAULT_ADMIN_NAME = "Executive";
const DEFAULT_ADMIN_DESCRIPTION =
  "Leads executive oversight and strategic decisions.";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatStaffName = (staff) => {
  if (!staff) {
    return "Unnamed staff";
  }

  if (staff.fullName) {
    return staff.fullName;
  }

  return (
    `${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
    staff.name ||
    "Unnamed staff"
  );
};

const normalizePositions = (items) => {
  return [...items].sort((left, right) => {
    const leftIsExecutive =
      `${left.positionName || ""}`.trim().toLowerCase() ===
      DEFAULT_ADMIN_NAME.toLowerCase();
    const rightIsExecutive =
      `${right.positionName || ""}`.trim().toLowerCase() ===
      DEFAULT_ADMIN_NAME.toLowerCase();

    if (leftIsExecutive && !rightIsExecutive) return -1;
    if (!leftIsExecutive && rightIsExecutive) return 1;

    const leftDate = left.createdAt ? new Date(left.createdAt).getTime() : 0;
    const rightDate = right.createdAt ? new Date(right.createdAt).getTime() : 0;
    return rightDate - leftDate;
  });
};

const Admins = () => {
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();

  const [adminGroups, setAdminGroups] = useState([]);
  const [activeAdminId, setActiveAdminId] = useState("");
  const [activeAdmin, setActiveAdmin] = useState(null);
  const [adminMembers, setAdminMembers] = useState([]);
  const [adminPermissions, setAdminPermissions] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
  const [isPermissionDeleteModalOpen, setIsPermissionDeleteModalOpen] =
    useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [selectedPermissionLoading, setSelectedPermissionLoading] =
    useState(false);
  const [assigningMemberId, setAssigningMemberId] = useState("");
  const [adminActionLoading, setAdminActionLoading] = useState(false);
  const [adminErrorMessage, setAdminErrorMessage] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({
    positionName: "",
    positionDescription: "",
  });

  const currentAdminName = activeAdmin?.positionName || DEFAULT_ADMIN_NAME;
  const currentAdminDescription =
    activeAdmin?.positionDescription || DEFAULT_ADMIN_DESCRIPTION;
  const currentAdminCreatedAt = formatDate(activeAdmin?.createdAt);
  const currentStaffIds = useMemo(
    () => new Set(adminMembers.map((member) => member.id)),
    [adminMembers],
  );

  const closeAdminModals = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setFormErrorMessage("");
  };

  const loadAdminDetails = useCallback(
    async (positionId) => {
      if (!apiBaseUrl || !positionId) {
        setActiveAdmin(null);
        setAdminMembers([]);
        setAdminPermissions([]);
        return;
      }

      try {
        setIsLoadingDetails(true);
        setAdminErrorMessage("");

        const [positionResponse, staffResponse, permissionsResponse] =
          await Promise.all([
            axios.get(`${apiBaseUrl}/organizations/positions/${positionId}`),
            axios.get(
              `${apiBaseUrl}/organizations/positions/${positionId}/staff`,
            ),
            axios.get(
              `${apiBaseUrl}/organizations/positions/${positionId}/permissions`,
            ),
          ]);

        setActiveAdmin(positionResponse?.data?.position || null);
        setAdminMembers(
          Array.isArray(staffResponse?.data?.staff)
            ? staffResponse.data.staff
            : [],
        );
        setAdminPermissions(
          Array.isArray(permissionsResponse?.data?.permissions)
            ? permissionsResponse.data.permissions
            : [],
        );
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load the selected admin group.";
        setAdminErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
        setActiveAdmin(null);
        setAdminMembers([]);
        setAdminPermissions([]);
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [apiBaseUrl],
  );

  const loadAdminGroups = useCallback(
    async (preferredAdminId = "") => {
      if (!apiBaseUrl) {
        setAdminErrorMessage("API base URL is not configured.");
        return;
      }

      try {
        setIsLoadingAdmins(true);
        setAdminErrorMessage("");

        const response = await axios.get(
          `${apiBaseUrl}/organizations/positions`,
        );
        let nextGroups = Array.isArray(response?.data?.positions)
          ? response.data.positions
          : [];

        if (nextGroups.length === 0) {
          const createResponse = await axios.post(
            `${apiBaseUrl}/organizations/positions`,
            {
              positionName: DEFAULT_ADMIN_NAME,
              positionDescription: DEFAULT_ADMIN_DESCRIPTION,
            },
          );

          const createdPosition = createResponse?.data?.position || null;
          nextGroups = createdPosition ? [createdPosition] : [];
        }

        const sortedGroups = normalizePositions(nextGroups);
        setAdminGroups(sortedGroups);

        const nextActiveId =
          preferredAdminId &&
          sortedGroups.some((group) => group.id === preferredAdminId)
            ? preferredAdminId
            : sortedGroups[0]?.id || "";

        setActiveAdminId(nextActiveId);
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load admin groups. Please try again.";
        setAdminErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
      } finally {
        setIsLoadingAdmins(false);
      }
    },
    [apiBaseUrl],
  );

  const loadAvailableEmployees = useCallback(async () => {
    if (!apiBaseUrl) {
      setAdminErrorMessage("API base URL is not configured.");
      return;
    }

    try {
      setIsLoadingEmployees(true);

      const response = await axios.get(`${apiBaseUrl}/organizations/employees`);
      const employees = Array.isArray(response?.data?.employees)
        ? response.data.employees
        : [];

      const normalizedEmployees = employees
        .map((employee) => ({
          ...employee,
          fullName: employee.fullName || employee.name || "",
          officialEmail: employee.officialEmail || employee.emailAddress || "",
          personalEmail: employee.personalEmail || employee.emailAddress || "",
          employeeNumber: employee.employeeNumber || employee.idNo || "",
          profilePhotoUrl: employee.profilePhotoUrl || employee.image || "",
          assignmentLabel:
            employee.assignmentLabel || employee.department || currentAdminName,
        }))
        .filter((employee) => employee.id && !currentStaffIds.has(employee.id));

      setAvailableEmployees(normalizedEmployees);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to load employees for admin assignment.";
      setAdminErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
      setAvailableEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [apiBaseUrl, currentAdminName, currentStaffIds]);

  useEffect(() => {
    loadAdminGroups();
  }, [loadAdminGroups]);

  useEffect(() => {
    if (activeAdminId) {
      loadAdminDetails(activeAdminId);
    }
  }, [activeAdminId, loadAdminDetails]);

  const openCreateModal = () => {
    setAdminForm({ positionName: "", positionDescription: "" });
    setFormErrorMessage("");
    setIsCreateModalOpen(true);
  };

  const openEditModal = () => {
    if (!activeAdmin) return;

    setAdminForm({
      positionName: activeAdmin.positionName || "",
      positionDescription: activeAdmin.positionDescription || "",
    });
    setFormErrorMessage("");
    setIsEditModalOpen(true);
  };

  const openDeleteModal = () => {
    setFormErrorMessage("");
    setIsDeleteModalOpen(true);
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();

    if (!apiBaseUrl) {
      setFormErrorMessage("API base URL is not configured.");
      return;
    }

    if (
      !adminForm.positionName.trim() ||
      !adminForm.positionDescription.trim()
    ) {
      setFormErrorMessage("Admin name and description are required.");
      return;
    }

    try {
      setAdminActionLoading(true);
      setFormErrorMessage("");

      const response = await axios.post(
        `${apiBaseUrl}/organizations/positions`,
        {
          positionName: adminForm.positionName.trim(),
          positionDescription: adminForm.positionDescription.trim(),
        },
      );

      const createdPosition = response?.data?.position || null;
      closeAdminModals();
      setSuccessMessage(
        `${createdPosition?.positionName || adminForm.positionName.trim()} admin created successfully.`,
      );
      setIsSuccessOpen(true);
      await loadAdminGroups(createdPosition?.id || "");
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create admin group. Please try again.";
      setFormErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleUpdateAdmin = async (event) => {
    event.preventDefault();

    if (!apiBaseUrl || !activeAdminId) {
      setFormErrorMessage("Unable to update this admin right now.");
      return;
    }

    if (
      !adminForm.positionName.trim() ||
      !adminForm.positionDescription.trim()
    ) {
      setFormErrorMessage("Admin name and description are required.");
      return;
    }

    try {
      setAdminActionLoading(true);
      setFormErrorMessage("");

      await axios.patch(
        `${apiBaseUrl}/organizations/positions/${activeAdminId}`,
        {
          positionName: adminForm.positionName.trim(),
          positionDescription: adminForm.positionDescription.trim(),
        },
      );

      closeAdminModals();
      setSuccessMessage("Admin group updated successfully.");
      setIsSuccessOpen(true);
      await loadAdminGroups(activeAdminId);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to update admin group. Please try again.";
      setFormErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!apiBaseUrl || !activeAdminId) {
      setFormErrorMessage("Unable to delete this admin right now.");
      return;
    }

    try {
      setAdminActionLoading(true);
      setFormErrorMessage("");

      await axios.delete(
        `${apiBaseUrl}/organizations/positions/${activeAdminId}`,
      );
      closeAdminModals();
      setSuccessMessage("Admin group deleted successfully.");
      setIsSuccessOpen(true);
      sessionStorage.removeItem("selectedPositionId");
      await loadAdminGroups();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to delete admin group. Please try again.";
      setFormErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setAdminActionLoading(false);
    }
  };

  const openMembersModal = () => {
    if (!activeAdmin) return;

    setFormErrorMessage("");
    setIsMembersModalOpen(true);
  };

  const openAddMembersModal = async () => {
    if (!activeAdmin) return;

    setFormErrorMessage("");
    setIsAddMembersModalOpen(true);
    await loadAvailableEmployees();
  };

  const handleAssignMember = async (employeeId) => {
    if (!apiBaseUrl || !activeAdminId || !employeeId) {
      return;
    }

    try {
      setAssigningMemberId(employeeId);
      setFormErrorMessage("");

      await axios.patch(
        `${apiBaseUrl}/organizations/employees/${encodeURIComponent(employeeId)}`,
        {
          positionId: activeAdminId,
        },
      );

      await Promise.all([
        loadAdminDetails(activeAdminId),
        loadAvailableEmployees(),
      ]);
      setSuccessMessage("Member added to admin group successfully.");
      setIsSuccessOpen(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to add this member to the admin group.";
      setFormErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setAssigningMemberId("");
    }
  };

  const openPermissionDeleteModal = (permission) => {
    setSelectedPermission(permission);
    setSelectedPermissionLoading(false);
    setFormErrorMessage("");
    setIsPermissionDeleteModalOpen(true);
  };

  const handleDeletePermission = async () => {
    if (!apiBaseUrl || !selectedPermission?.id) {
      return;
    }

    try {
      setSelectedPermissionLoading(true);

      await axios.delete(
        `${apiBaseUrl}/organizations/permissions/${selectedPermission.id}`,
      );
      setIsPermissionDeleteModalOpen(false);
      setSelectedPermission(null);
      await loadAdminDetails(activeAdminId);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete this permission.";
      setFormErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setSelectedPermissionLoading(false);
    }
  };

  const selectedAdminMembers = adminMembers.slice(0, 4);
  const permissionPreviewLabel = activeAdminId
    ? `${currentAdminName} permissions`
    : "Permissions";

  return (
    <div className="min-h-screen p-6 font-Nunito">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Admins" },
        ]}
        title="Admins"
        subtitle="Manage admin groups, members, and permissions."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 rounded-xl border border-dashed border-[#1234CD] bg-white px-4 py-2 text-sm font-medium text-[#1234CD] hover:bg-[#EEF2FF] dashboard-button-hover"
          >
            <FaPlus size={12} />
            Add admin
          </button>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-3 dashboard-enter">
        {isLoadingAdmins && adminGroups.length === 0 ? (
          <span className="text-sm text-gray-500">Loading admin groups...</span>
        ) : null}
      </div>

      {adminErrorMessage ? (
        <div
          className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dashboard-fade-in"
          role="alert"
        >
          {adminErrorMessage}
        </div>
      ) : null}

      <div className="mb-6 flex flex-wrap gap-3 overflow-x-auto pb-2">
        {adminGroups.map((group, index) => {
          const isActive = group.id === activeAdminId;

          return (
            <button
              key={group.id}
              type="button"
              onClick={() => setActiveAdminId(group.id)}
              className={`rounded-xl border px-4 py-2 text-sm font-medium transition stagger-item ${
                isActive
                  ? "border-[#1234CD] bg-[#1234CD] text-white shadow-sm"
                  : "border-[#C8D0F8] bg-white text-[#071C50] hover:border-[#1234CD] hover:text-[#1234CD]"
              }`}
            >
              {group.positionName || DEFAULT_ADMIN_NAME}
            </button>
          );
        })}

        {!isLoadingAdmins && adminGroups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-2 text-sm text-gray-500">
            No admin groups yet.
          </div>
        ) : null}
      </div>

      {isLoadingDetails && !activeAdmin ? (
        <FetchLoadingState
          message="Loading admin details..."
          className="min-h-[50vh]"
        />
      ) : null}

      {!isLoadingDetails && activeAdmin ? (
        <>
          <div className="dashboard-enter dashboard-enter-delay-1">
            <InfoCard
              details="Admin Details"
              title={currentAdminName}
              description={currentAdminDescription}
              metaData={[
                { label: "Date created", value: currentAdminCreatedAt },
                {
                  label: "Total staff",
                  value: activeAdmin?.totalStaff ?? adminMembers.length ?? 0,
                },
                { label: "Admin ID", value: activeAdmin?.id || "-" },
              ]}
              role={`Delete ${currentAdminName}`}
              update="Update admin"
              onDelete={openDeleteModal}
              onUpdate={openEditModal}
            />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dashboard-enter dashboard-enter-delay-2 dashboard-card-hover">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-[#071C50]">
                    Members
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {adminMembers.length} staff member
                    {adminMembers.length === 1 ? "" : "s"} assigned to{" "}
                    {currentAdminName}.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={openMembersModal}
                    className="rounded-lg border border-[#1234CD] px-3 py-2 text-sm font-medium text-[#1234CD] dashboard-button-hover"
                  >
                    View all
                  </button>
                  <button
                    type="button"
                    onClick={openAddMembersModal}
                    className="rounded-lg bg-[#1234CD] px-3 py-2 text-sm font-medium text-white dashboard-button-hover"
                  >
                    Add members
                  </button>
                </div>
              </div>

              {adminMembers.length === 0 ? (
                <div className="mt-6 rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dashboard-fade-in">
                  No members assigned to this admin group yet.
                </div>
              ) : (
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {selectedAdminMembers.map((member) => (
                    <div
                      key={member.id}
                      className="rounded-xl border border-gray-200 p-4 stagger-item dashboard-card-hover"
                    >
                      <p className="font-semibold text-[#071C50]">
                        {formatStaffName(member)}
                      </p>
                      <p className="mt-1 text-sm text-gray-600">
                        {member.assignmentLabel ||
                          member.positionName ||
                          "Staff"}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {member.officialEmail ||
                          member.personalEmail ||
                          member.phone ||
                          "No contact info"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dashboard-enter dashboard-enter-delay-3 dashboard-card-hover">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-[#071C50]">
                    Permission list
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Editable permissions for {permissionPreviewLabel}.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    navigate("/company-settings/new-permission", {
                      state: { positionId: activeAdminId },
                    })
                  }
                  className="rounded-lg bg-[#1234CD] px-3 py-2 text-sm font-medium text-white dashboard-button-hover"
                >
                  Add permission
                </button>
              </div>

              {adminPermissions.length === 0 ? (
                <div className="mt-6 rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dashboard-fade-in">
                  No permissions assigned to this admin group yet.
                </div>
              ) : (
                <div className="mt-5 overflow-x-auto">
                  <table className="min-w-[640px] w-full text-left">
                    <thead>
                      <tr className="text-sm text-gray-500">
                        <th className="py-3 pr-4 font-medium">Permission</th>
                        <th className="py-3 pr-4 font-medium">Modules</th>
                        <th className="py-3 pr-4 font-medium">Description</th>
                        <th className="py-3 pr-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminPermissions.map((permission) => {
                        const permissionModules = getModuleLabels(
                          Array.isArray(permission.components)
                            ? permission.components
                            : permission.component
                              ? [permission.component]
                              : [],
                        );

                        return (
                          <tr
                            key={permission.id}
                            className="border-t border-gray-100 align-top table-row-enter"
                          >
                            <td className="py-4 pr-4 text-sm font-medium text-[#071C50]">
                              {permission.permissionName}
                            </td>
                            <td className="py-4 pr-4 text-sm text-gray-600">
                              <div className="flex flex-wrap gap-2">
                                {permissionModules.length > 0 ? (
                                  permissionModules.map((moduleName) => (
                                    <span
                                      key={`${permission.id}-${moduleName}`}
                                      className="rounded-full bg-[#EEF2FF] px-3 py-1 text-xs font-medium text-[#1234CD]"
                                    >
                                      {moduleName}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    No modules selected
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 pr-4 text-sm text-gray-600">
                              {permission.description}
                            </td>
                            <td className="py-4 pr-4 text-sm">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      {
                                        pathname:
                                          "/company-settings/permissionview-details",
                                        search: `?permissionId=${encodeURIComponent(permission.id)}`,
                                      },
                                      {
                                        state: { permissionId: permission.id },
                                      },
                                    )
                                  }
                                  className="rounded-lg border border-[#1234CD] px-3 py-2 font-medium text-[#1234CD] dashboard-button-hover"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    openPermissionDeleteModal(permission)
                                  }
                                  className="rounded-lg border border-[#EF4444] px-3 py-2 font-medium text-[#EF4444] dashboard-button-hover"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </>
      ) : null}

      {!isLoadingDetails && !activeAdmin && !adminErrorMessage ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500 dashboard-fade-in">
          Select an admin group to manage members and permissions.
        </div>
      ) : null}

      <InviteModal
        isOpen={isCreateModalOpen}
        onClose={closeAdminModals}
      >
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Create Admin</p>
          <p className="mt-2 text-sm text-gray-600">
            Create a new admin group and assign members or permissions to it.
          </p>

          <form
            className="my-8"
            onSubmit={handleCreateAdmin}
          >
            {formErrorMessage ? (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {formErrorMessage}
              </p>
            ) : null}

            <div className="mb-4">
              <label className="mb-2 block text-[14px] font-medium text-black">
                Admin name
              </label>
              <input
                type="text"
                value={adminForm.positionName}
                onChange={(event) =>
                  setAdminForm((prev) => ({
                    ...prev,
                    positionName: event.target.value,
                  }))
                }
                className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                placeholder="e.g. Executive"
              />
            </div>

            <div className="mb-6">
              <label className="mb-2 block text-[14px] font-medium text-black">
                Description
              </label>
              <textarea
                value={adminForm.positionDescription}
                onChange={(event) =>
                  setAdminForm((prev) => ({
                    ...prev,
                    positionDescription: event.target.value,
                  }))
                }
                className="min-h-32 w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
                placeholder="Short description for this admin group"
              />
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={closeAdminModals}
                className="w-40 rounded-xl border border-[#1234CD] px-4 py-3 font-medium text-[#1234CD] dashboard-button-hover"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={adminActionLoading}
                className="w-40 rounded-xl bg-[#1234CD] px-4 py-3 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 dashboard-button-hover"
              >
                {adminActionLoading ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <InviteModal
        isOpen={isEditModalOpen}
        onClose={closeAdminModals}
      >
        <div className="my-8 font-Outfit">
          <p className="text-2xl font-semibold text-black">Edit Admin</p>
          <p className="mt-2 text-sm text-gray-600">
            Update the selected admin group details.
          </p>

          <form
            className="my-8"
            onSubmit={handleUpdateAdmin}
          >
            {formErrorMessage ? (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {formErrorMessage}
              </p>
            ) : null}

            <div className="mb-4">
              <label className="mb-2 block text-[14px] font-medium text-black">
                Admin name
              </label>
              <input
                type="text"
                value={adminForm.positionName}
                onChange={(event) =>
                  setAdminForm((prev) => ({
                    ...prev,
                    positionName: event.target.value,
                  }))
                }
                className="w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
              />
            </div>

            <div className="mb-6">
              <label className="mb-2 block text-[14px] font-medium text-black">
                Description
              </label>
              <textarea
                value={adminForm.positionDescription}
                onChange={(event) =>
                  setAdminForm((prev) => ({
                    ...prev,
                    positionDescription: event.target.value,
                  }))
                }
                className="min-h-32 w-full rounded-xl border-2 border-[#F3F5F5] p-3 focus:outline-[#1234CD]"
              />
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={closeAdminModals}
                className="w-40 rounded-xl border border-[#1234CD] px-4 py-3 font-medium text-[#1234CD] dashboard-button-hover"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={adminActionLoading}
                className="w-40 rounded-xl bg-[#1234CD] px-4 py-3 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 dashboard-button-hover"
              >
                {adminActionLoading ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <DeleteItem
        isOpen={isDeleteModalOpen}
        onClose={closeAdminModals}
      >
        <div>
          <img
            className="mx-auto w-40 animate-pulse"
            src={DeleteImg}
            alt="delete admin"
          />
          <h1 className="text-center text-lg font-bold text-[#000000]">
            Continue to delete this admin group?
          </h1>
          <p className="mt-2 text-center text-sm">Action cannot be undone.</p>
          {formErrorMessage ? (
            <p
              className="mt-3 text-center text-sm text-red-600"
              role="alert"
            >
              {formErrorMessage}
            </p>
          ) : null}
        </div>
        <div className="my-4 flex items-center gap-4 py-4">
          <button
            type="button"
            onClick={closeAdminModals}
            className="w-[102px] rounded-[10px] border-2 border-[#1233CC] px-4 py-1 font-medium text-[#1233CC] dashboard-button-hover"
          >
            No
          </button>
          <button
            type="button"
            className="w-[102px] rounded-[10px] bg-[#1233CC] px-4 py-1 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 dashboard-button-hover"
            onClick={handleDeleteAdmin}
            disabled={adminActionLoading}
          >
            {adminActionLoading ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <StaffListModal
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        title={`Members of ${currentAdminName}`}
        subtitle="Employees currently assigned to this admin group."
        staff={adminMembers}
        loading={isLoadingDetails}
        error={adminErrorMessage}
        onRefresh={() => loadAdminDetails(activeAdminId)}
      />

      <StaffListModal
        isOpen={isAddMembersModalOpen}
        onClose={() => setIsAddMembersModalOpen(false)}
        title={`Add members to ${currentAdminName}`}
        subtitle="Select approved employees to assign to this admin group."
        staff={availableEmployees}
        loading={isLoadingEmployees}
        error={adminErrorMessage}
        onRefresh={loadAvailableEmployees}
        primaryActionLabel="Add"
        onPrimaryAction={handleAssignMember}
        primaryActionLoadingId={assigningMemberId}
      />

      <DeleteItem
        isOpen={isPermissionDeleteModalOpen}
        onClose={() => {
          setIsPermissionDeleteModalOpen(false);
          setSelectedPermission(null);
        }}
      >
        <div>
          <img
            className="mx-auto w-40 animate-pulse"
            src={DeleteImg}
            alt="delete permission"
          />
          <h1 className="text-center text-lg font-bold text-[#000000]">
            Continue to delete this permission?
          </h1>
          <p className="mt-2 text-center text-sm">Action cannot be undone.</p>
          {formErrorMessage ? (
            <p
              className="mt-3 text-center text-sm text-red-600"
              role="alert"
            >
              {formErrorMessage}
            </p>
          ) : null}
        </div>
        <div className="my-4 flex items-center gap-4 py-4">
          <button
            type="button"
            onClick={() => {
              setIsPermissionDeleteModalOpen(false);
              setSelectedPermission(null);
            }}
            className="w-[102px] rounded-[10px] border-2 border-[#1233CC] px-4 py-1 font-medium text-[#1233CC] dashboard-button-hover"
          >
            No
          </button>
          <button
            type="button"
            className="w-[102px] rounded-[10px] bg-[#1233CC] px-4 py-1 font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 dashboard-button-hover"
            onClick={handleDeletePermission}
            disabled={selectedPermissionLoading}
          >
            {selectedPermissionLoading ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessModal
        isOpen={isSuccessOpen}
        onClose={() => setIsSuccessOpen(false)}
      >
        <div className="flex flex-col items-center gap-4">
          <img
            src={verifyImg}
            alt="success"
            className="h-16 w-16"
          />
          <p className="text-lg font-semibold text-green-600">
            {successMessage}
          </p>
        </div>
      </SuccessModal>
    </div>
  );
};

export default Admins;
